import { css, keyframes } from '@emotion/css';
import {
  appColors,
  breakpoints,
  fontFamily,
  shadows,
} from '../../styles/variables.style';

const fadeInLeft = keyframes`
  from { opacity: 0; transform: translateX(-50px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeInRight = keyframes`
  from { opacity: 0; transform: translateX(50px); }
  to { opacity: 1; transform: translateX(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const slowRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const pageWrapper = css`
  background-color: ${appColors.lightGray};
  min-height: 100vh;
  padding-bottom: 100px;
  overflow-x: hidden;
`;

export const heroContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 80vh;
  position: relative;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(0,0,0,0.05) 1px, transparent 1px);
    background-size: 100px 100px;
    z-index: -1;
    mask-image: radial-gradient(circle at center, black, transparent 80%);
  }
`;

export const heroContent = css`
  max-width: 800px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const maskContainer = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  height: 90%;
  max-width: 900px;
  max-height: 600px;
  z-index: 1;
  opacity: 0.15;
  filter: grayscale(100%) contrast(150%);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    clip-path: polygon(20% 0%, 80% 0%, 100% 20%, 100% 80%, 80% 100%, 20% 100%, 0% 80%, 0% 20%);
  }
`;

export const heroLeft = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeInLeft} 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
  width: 100%;
`;

export const welcomeText = css`
  font-family: ${fontFamily.poppins};
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${appColors.gray800};
  margin-bottom: 20px;
  font-weight: 700;
`;

export const headline = css`
  font-family: 'Playfair Display', serif;
  font-size: 3.5rem;
  font-weight: 500;
  margin: 0 0 1.5rem;
  line-height: 1.1;
  color: ${appColors.gray800};
  letter-spacing: -1px;
  text-transform: uppercase;

  span {
    color: ${appColors.orange};
    font-style: italic;
  }

  @media (min-width: ${breakpoints.lg.up}) {
    font-size: 5rem;
    margin: 0 0 2rem;
  }
`;

export const line = css`
  font-family: ${fontFamily.poppins};
  font-size: 1rem;
  color: ${appColors.gray800};
  margin: 0 0 2.5rem;
  line-height: 1.8;
  max-width: 600px;
  opacity: 0.8;

  @media (min-width: ${breakpoints.lg.up}) {
    font-size: 1.1rem;
  }
`;

export const heroRight = css`
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeInRight} 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
`;

export const decorationCard1 = css`
  position: absolute;
  width: 280px;
  height: 320px;
  background: ${appColors.orange};
  border-radius: 40px;
  transform: rotate(-15deg);
  z-index: -1;
  opacity: 0.8;
  box-shadow: ${shadows.neumorphic};

  @media (max-width: ${breakpoints.md.down}) {
    width: 200px;
    height: 240px;
  }
`;

export const decorationCard2 = css`
  position: absolute;
  width: 300px;
  height: 340px;
  background: transparent;
  border: 2px solid ${appColors.orange};
  border-radius: 40px;
  transform: rotate(10deg);
  z-index: -2;
  opacity: 0.3;

  @media (max-width: ${breakpoints.md.down}) {
    width: 220px;
    height: 260px;
  }
`;

export const floatingShape = css`
  position: absolute;
  color: ${appColors.orange};
  font-size: 24px;
  font-weight: 700;
  opacity: 0.6;
  z-index: 1;
  animation: ${float} 4s ease-in-out infinite;

  &.shape-plus {
    top: 10%;
    right: 10%;
    font-size: 40px;
    animation-delay: 0.5s;
  }

  &.shape-triangle {
    bottom: 10%;
    left: 10%;
    font-size: 30px;
    animation-delay: 1.2s;
  }
`;

export const heroImage = css`
  width: 100%;
  max-width: 450px;
  height: auto;
  position: relative;
  z-index: 2;
  filter: drop-shadow(20px 30px 50px rgba(0,0,0,0.15));
  border-radius: 20px;

  @media (max-width: ${breakpoints.lg.down}) {
    max-width: 300px;
  }
`;

export const sectionHeading = css`
  font-family: ${fontFamily.poppins};
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 50px;
  color: ${appColors.gray800};
`;

export const challengeGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 28px;
  max-width: 1300px;
  margin: 0 auto;
  padding: 20px 20px 80px;
`;

export const challengeCard = css`
  background: #ffffff;
  border-radius: 4px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03);
  transition: all 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  border: 1px solid rgba(0,0,0,0.05);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 40px rgba(0,0,0,0.08);
    text-decoration: none;
    border-color: ${appColors.orange};
  }
`;

export const glowWrapper = css`
  width: 100%;
  height: 100%;
  overflow: visible !important;
`;

export const challengeCardGlow = css`
  width: 100%;
  height: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  background: transparent;
  position: relative;
  transition: transform 0.3s ease;

  &:hover {
    text-decoration: none;
    transform: translateY(-5px);
  }
`;

export const cardImage = css`
  width: 100%;
  height: 200px;
  object-fit: contain;
  background: #f8f9fa;
  border-radius: 15px;
  margin-bottom: 20px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
`;

export const cardTitle = css`
  font-family: ${fontFamily.poppins};
  font-size: 1.5rem;
  font-weight: 700;
  color: ${appColors.gray800};
  margin-bottom: 10px;
  text-align: center;
`;

export const cardCount = css`
  font-family: ${fontFamily.poppins};
  font-size: 0.85rem;
  color: ${appColors.gray500};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-top: 5px;
`;

export const loaderWrapper = css`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

export const emptyText = css`
  text-align: center;
  font-family: ${fontFamily.poppins};
  font-size: 1.2rem;
  color: ${appColors.gray500};
  margin-top: 50px;
`;
